import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  Brain,
  Briefcase,
  CheckCircle2,
  Code2,
  Copy,
  Cpu,
  Eye,
  FileText,
  ImageIcon,
  RefreshCw,
  Send,
  Sparkles,
  Square,
  Target,
  Type,
  Video,
} from 'lucide-react';
import PageTitle from '../components/PageTitle';
import { WorkspaceShell } from '../components/layout';
import { Model } from '../types/chat';
import { getModelBrandIcon } from '../utils/modelBrandIcon';

type ArenaStatus = 'idle' | 'running' | 'done' | 'error';
type IconComponent = ComponentType<{ className?: string }>;

type ArenaResponse = {
  modelUuid: string;
  modelId: string;
  status: ArenaStatus;
  content: string;
  reasoning: string;
  error?: string;
  elapsedMs?: number;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
};

const MIN_SELECT = 2;
const MAX_SELECT = 3;

const SCENARIO_ICONS: Record<string, IconComponent> = {
  code: Code2,
  business: Briefcase,
  paper: FileText,
  writing: Sparkles,
  reasoning: Brain,
  decision: Target,
};

function getApiUrl(path: string): string {
  if (import.meta.env.VITE_API_URL && import.meta.env.PROD) {
    return `${import.meta.env.VITE_API_URL}${path}`;
  }
  return path;
}

function formatTokenCount(value?: number): string {
  return typeof value === 'number' ? value.toLocaleString() : '-';
}

function formatElapsed(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatContextWindow(value?: number): string {
  if (!value || value <= 0) return '';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
}

function getRealModelId(model?: Model): string | undefined {
  return model?.upstreamModelId || model?.canonical_slug || undefined;
}

function createInitialResponses(selected: Model[]): ArenaResponse[] {
  return selected.map(model => ({
    modelUuid: model.uuid,
    modelId: model.id,
    status: 'idle' as ArenaStatus,
    content: '',
    reasoning: '',
  }));
}

export default function Arena() {
  const { t } = useTranslation(['arena', 'common']);
  const [models, setModels] = useState<Model[]>([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [prompt, setPrompt] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [responses, setResponses] = useState<ArenaResponse[]>([]);
  const [running, setRunning] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadModels() {
      try {
        setLoadingModels(true);
        const response = await fetch(getApiUrl('/api/models'), { credentials: 'include' });
        if (!response.ok) throw new Error('Failed to load models');
        const data = await response.json();
        const availableModels: Model[] = data.models || data.data || [];
        if (cancelled) return;
        setModels(availableModels);
      } catch (error: any) {
        if (!cancelled) toast.error(error.message || t('load_failed'));
      } finally {
        if (!cancelled) setLoadingModels(false);
      }
    }

    loadModels();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const selectedModels = useMemo(
    () => selectedUuids.flatMap(uuid => {
      const model = models.find(item => item.uuid === uuid);
      return model ? [model] : [];
    }),
    [selectedUuids, models],
  );
  const selectionFull = selectedUuids.length >= MAX_SELECT;
  const canRun = prompt.trim().length > 0 && selectedModels.length >= MIN_SELECT && !running;

  const toggleModel = (uuid: string) => {
    if (running) return;
    setSelectedUuids(prev => {
      if (prev.includes(uuid)) return prev.filter(id => id !== uuid);
      if (prev.length >= MAX_SELECT) return prev;
      return [...prev, uuid];
    });
  };

  useEffect(() => {
    if (running) return;
    setResponses(prev => {
      const hasContent = prev.some(item => item.content || item.reasoning || item.error || item.status !== 'idle');
      if (hasContent) return prev;
      return createInitialResponses(selectedModels);
    });
  }, [selectedModels, running]);

  const scenarioItems = useMemo(() => {
    return ['code', 'business', 'paper', 'writing', 'reasoning', 'decision'].map(key => ({
      key,
      label: t(`scenarios.${key}.label`),
      prompt: t(`scenarios.${key}.prompt`),
      icon: SCENARIO_ICONS[key],
    }));
  }, [t]);

  const updateResponse = (modelUuid: string, updater: (response: ArenaResponse) => ArenaResponse) => {
    setResponses(prev => prev.map(response => response.modelUuid === modelUuid ? updater(response) : response));
  };

  const copyResponse = async (response: ArenaResponse) => {
    await navigator.clipboard.writeText(response.content);
    toast.success(t('common:copied_to_clipboard'));
  };

  const stopRun = () => {
    abortControllerRef.current?.abort();
    setRunning(false);
  };

  const applyScenario = (scenarioPrompt: string) => {
    if (running) return;
    setPrompt(scenarioPrompt);
    setResponses(createInitialResponses(selectedModels));
  };

  const runArena = async () => {
    if (!canRun) return;

    const initialResponses = createInitialResponses(selectedModels).map(response => ({
      ...response,
      status: 'running' as ArenaStatus,
    }));
    setResponses(initialResponses);
    setRunning(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(getApiUrl('/api/arena/runs'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          modelUuids: selectedModels.map(model => model.uuid),
          messages: [{ role: 'user', content: prompt.trim() }],
          temperature,
          max_tokens: maxTokens,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || t('run_failed'));
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error(t('run_failed'));

      const decoder = new TextDecoder();
      let buffer = '';
      let doneReading = false;

      while (!doneReading) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() || '';

        for (const block of blocks) {
          const line = block.split('\n').find(item => item.startsWith('data: '));
          if (!line) continue;
          const data = line.slice(6);
          if (data === '[DONE]') {
            doneReading = true;
            break;
          }

          const event = JSON.parse(data);
          if (event.type === 'model_start') {
            updateResponse(event.modelUuid, item => ({ ...item, status: 'running', modelId: event.modelId || item.modelId }));
          }
          if (event.type === 'model_delta') {
            updateResponse(event.modelUuid, item => ({ ...item, content: `${item.content}${event.delta}` }));
          }
          if (event.type === 'model_reasoning_delta') {
            updateResponse(event.modelUuid, item => ({ ...item, reasoning: `${item.reasoning}${event.delta}` }));
          }
          if (event.type === 'model_done') {
            updateResponse(event.modelUuid, item => ({
              ...item,
              status: 'done',
              content: event.content || item.content,
              reasoning: event.reasoning || item.reasoning,
              usage: event.usage,
              elapsedMs: event.elapsedMs,
            }));
          }
          if (event.type === 'model_error') {
            updateResponse(event.modelUuid, item => ({ ...item, status: 'error', error: event.error || t('model_failed') }));
          }
          if (event.type === 'run_error') throw new Error(event.error || t('run_failed'));
        }
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        toast.error(error.message || t('run_failed'));
        setResponses(prev => prev.map(item => item.status === 'running' ? { ...item, status: 'error', error: error.message || t('run_failed') } : item));
      }
    } finally {
      setRunning(false);
      abortControllerRef.current = null;
    }
  };

  const renderCaps = (model: Model) => {
    const inputMods = model.input_modalities || [];
    const outputMods = model.output_modalities || [];
    const hasVision = inputMods.includes('image');
    const hasVideo = inputMods.includes('video');
    const hasFileUpload = inputMods.includes('file');
    const hasImageGen = outputMods.includes('image');
    return (
      <>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 whitespace-nowrap">
          <Type className="h-3 w-3" /> Text
        </span>
        {hasVision && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
            <Eye className="h-3 w-3" /> Vision
          </span>
        )}
        {hasFileUpload && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 whitespace-nowrap">
            <FileText className="h-3 w-3" /> File
          </span>
        )}
        {hasVideo && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 whitespace-nowrap">
            <Video className="h-3 w-3" /> Video
          </span>
        )}
        {hasImageGen && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
            <ImageIcon className="h-3 w-3" /> Image Gen
          </span>
        )}
      </>
    );
  };

  return (
    <WorkspaceShell
      title={t('title')}
      subtitle={t('subtitle')}
      sectionLabel={t('badge')}
      actions={(
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
          <span>{t('selected_count', { count: selectedModels.length, max: MAX_SELECT })}</span>
          {selectedModels.length > 0 && (
            <>
              <span className="h-4 w-px bg-border" />
              <span>{t('cost_hint', { count: selectedModels.length })}</span>
            </>
          )}
        </div>
      )}
    >
      <PageTitle titleKey="title" descriptionKey="subtitle" namespace="arena" />
      <div className="space-y-5">
        <section className="rounded-xl border border-border bg-card p-5 space-y-5">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-foreground">{t('select_models_title')}</h2>
              <span className="text-xs text-muted-foreground">{t('selected_count', { count: selectedModels.length, max: MAX_SELECT })}</span>
            </div>
            <p className="text-sm text-muted-foreground">{t('select_models_hint', { max: MAX_SELECT })}</p>
          </div>

          {loadingModels ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              {t('loading_models')}
            </div>
          ) : models.length === 0 ? (
            <div className="rounded-lg border border-border bg-background p-6 text-center text-sm text-muted-foreground">
              {t('no_models')}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {models.map(model => {
                const selected = selectedUuids.includes(model.uuid);
                const disabled = (!selected && selectionFull) || running;
                const realId = getRealModelId(model);
                const ctx = formatContextWindow(model.contextWindow);
                const brandIcon = getModelBrandIcon(model.id);
                return (
                  <button
                    key={model.uuid}
                    type="button"
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => toggleModel(model.uuid)}
                    className={`flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      selected ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border hover:bg-muted'
                    } ${disabled && !selected ? 'cursor-not-allowed opacity-50 hover:bg-transparent' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${selected ? 'bg-primary/15 text-primary' : brandIcon ? 'bg-white' : 'bg-muted text-muted-foreground'}`}>
                        {selected ? <CheckCircle2 className="h-4 w-4" /> : brandIcon ? <img src={brandIcon} alt="" className="h-5 w-5 object-contain" /> : <Cpu className="h-4 w-4" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-foreground" title={model.id}>{model.id}</div>
                        {realId && <div className="truncate text-xs text-muted-foreground" title={realId}>{realId}</div>}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {ctx && (
                        <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
                          {t('context_label')}: {ctx}
                        </span>
                      )}
                      {renderCaps(model)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-3 border-t border-border pt-4">
            <textarea
              value={prompt}
              onChange={event => setPrompt(event.target.value)}
              disabled={running}
              rows={6}
              placeholder={t('prompt_placeholder')}
              className="w-full resize-y rounded-lg border border-border bg-background px-3 py-3 text-sm leading-6 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            />
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {scenarioItems.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={running}
                    onClick={() => applyScenario(item.prompt)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-border pt-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="grid grid-cols-2 gap-3 sm:flex sm:items-center">
              <label className="text-xs text-muted-foreground">
                <span className="block mb-1">{t('temperature')}</span>
                <input
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  value={temperature}
                  disabled={running}
                  onChange={event => setTemperature(Number(event.target.value))}
                  className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground sm:w-24"
                />
              </label>
              <label className="text-xs text-muted-foreground">
                <span className="block mb-1">{t('max_tokens')}</span>
                <input
                  type="number"
                  min={256}
                  max={32000}
                  step={256}
                  value={maxTokens}
                  disabled={running}
                  onChange={event => setMaxTokens(Number(event.target.value))}
                  className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground sm:w-28"
                />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!running && selectedModels.length < MIN_SELECT && <span className="text-xs text-muted-foreground">{t('min_models_hint', { min: MIN_SELECT })}</span>}
              {running ? (
                <button onClick={stopRun} className="btn btn-secondary px-4 py-2 gap-2">
                  <Square className="h-4 w-4" />
                  {t('stop')}
                </button>
              ) : (
                <button disabled={!canRun} onClick={runArena} className="btn btn-primary px-4 py-2 gap-2">
                  <Send className="h-4 w-4" />
                  {t('run')}
                </button>
              )}
            </div>
          </div>
        </section>

        {selectedModels.length > 0 && (
          <section className={`grid gap-4 ${selectedModels.length >= 3 ? 'xl:grid-cols-3' : 'md:grid-cols-2'}`}>
            {selectedModels.map(model => {
              const response = responses.find(item => item.modelUuid === model.uuid) || createInitialResponses([model])[0];
              const isRunning = response?.status === 'running';
              const realId = getRealModelId(model);
              const ctx = formatContextWindow(model.contextWindow);
              const brandIcon = getModelBrandIcon(model.id);
              return (
                <article key={model.uuid} className="min-h-[34rem] rounded-xl border border-border bg-card flex flex-col overflow-hidden">
                  <header className="border-b border-border p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${brandIcon ? 'bg-white' : 'bg-muted text-muted-foreground'}`}>
                          {brandIcon ? <img src={brandIcon} alt="" className="h-5 w-5 object-contain" /> : <Cpu className="h-4 w-4" />}
                        </span>
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-semibold text-foreground" title={model.id}>{model.id}</h3>
                          {realId && <p className="truncate text-xs text-muted-foreground" title={realId}>{realId}</p>}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {isRunning && <RefreshCw className="h-4 w-4 animate-spin text-primary" />}
                        {response?.status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                        {response?.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                      </div>
                    </div>

                    {(ctx || model.input_modalities?.length || model.output_modalities?.length) && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {ctx && (
                          <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
                            {t('context_label')}: {ctx}
                          </span>
                        )}
                        {renderCaps(model)}
                      </div>
                    )}

                    <div className="grid grid-cols-5 gap-2 text-xs text-muted-foreground">
                      <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                        <div>{t('time')}</div>
                        <div className="mt-0.5 font-medium text-foreground">{formatElapsed(response?.elapsedMs)}</div>
                      </div>
                      <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                        <div>{t('tokens_prompt')}</div>
                        <div className="mt-0.5 font-medium text-foreground">{formatTokenCount(response?.usage?.prompt_tokens)}</div>
                      </div>
                      <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                        <div>{t('tokens_completion')}</div>
                        <div className="mt-0.5 font-medium text-foreground">{formatTokenCount(response?.usage?.completion_tokens)}</div>
                      </div>
                      <div className="rounded-lg bg-muted/50 px-2 py-1.5">
                        <div>{t('tokens_total')}</div>
                        <div className="mt-0.5 font-medium text-foreground">{formatTokenCount(response?.usage?.total_tokens)}</div>
                      </div>
                      <button
                        disabled={!response?.content}
                        onClick={() => response && copyResponse(response)}
                        className="rounded-lg bg-muted/50 px-2 py-1.5 text-left hover:bg-muted disabled:opacity-50"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        <span className="sr-only">{t('copy')}</span>
                      </button>
                    </div>
                  </header>

                  <div className="flex-1 p-4">
                    {response?.error ? (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{response.error}</div>
                    ) : response?.content ? (
                      <div className="prose prose-sm max-w-none dark:prose-invert prose-pre:whitespace-pre-wrap prose-pre:break-words">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{response.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <div className="h-full min-h-64 flex items-center justify-center text-center text-sm text-muted-foreground px-6">
                        {response?.status === 'running' ? t('waiting') : t('empty')}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </div>
    </WorkspaceShell>
  );
}
